import { useEffect, useCallback, useRef } from 'react';

/**
 * useHotkeys — Hook générique pour intercepter et gérer les raccourcis clavier.
 * 
 * @param {string|Array<string>} keys - Combinaison de touches (ex: 'ctrl+s', 'meta+s', ['ctrl+s', 'enter'])
 * @param {Function} callback - Fonction exécutée lors du déclenchement
 * @param {Object} options - Options de configuration ({ preventDefault: true, enabled: true })
 */
export const useHotkeys = (keys, callback, options = {}) => {
  const {
    preventDefault = true,
    stopPropagation = false,
    enabled = true,
    target = null // document par défaut
  } = options;

  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;

    const keyList = Array.isArray(keys) ? keys : [keys];
    const isCtrlOrCmd = event.ctrlKey || event.metaKey;
    const isShift = event.shiftKey;
    const isAlt = event.altKey;
    const pressedKey = event.key.toLowerCase();

    for (const keyCombo of keyList) {
      const parts = keyCombo.toLowerCase().split('+').map(p => p.trim());
      
      const requiresCtrl = parts.includes('ctrl') || parts.includes('control');
      const requiresCmd = parts.includes('cmd') || parts.includes('meta');
      const requiresCtrlOrCmd = parts.includes('ctrl/cmd') || parts.includes('mod');
      const requiresShift = parts.includes('shift');
      const requiresAlt = parts.includes('alt');
      
      const mainKey = parts.find(p => !['ctrl', 'control', 'cmd', 'meta', 'ctrl/cmd', 'mod', 'shift', 'alt'].includes(p));

      const ctrlCmdMatch = requiresCtrlOrCmd
        ? isCtrlOrCmd
        : (requiresCtrl === event.ctrlKey && requiresCmd === event.metaKey) || (requiresCtrl && isCtrlOrCmd);
      
      const shiftMatch = requiresShift ? isShift : !isShift;
      const altMatch = requiresAlt ? isAlt : !isAlt;

      let keyMatch = false;
      if (mainKey) {
        if (mainKey === 'enter' && pressedKey === 'enter') keyMatch = true;
        else if (mainKey === 'escape' && (pressedKey === 'escape' || pressedKey === 'esc')) keyMatch = true;
        else if (mainKey === 'tab' && pressedKey === 'tab') keyMatch = true;
        else if (mainKey === 'arrowup' && pressedKey === 'arrowup') keyMatch = true;
        else if (mainKey === 'arrowdown' && pressedKey === 'arrowdown') keyMatch = true;
        else if (mainKey === 'arrowleft' && pressedKey === 'arrowleft') keyMatch = true;
        else if (mainKey === 'arrowright' && pressedKey === 'arrowright') keyMatch = true;
        else if (mainKey === pressedKey) keyMatch = true;
      } else {
        keyMatch = true;
      }

      if (ctrlCmdMatch && shiftMatch && altMatch && keyMatch) {
        if (preventDefault) {
          event.preventDefault();
        }
        if (stopPropagation) {
          event.stopPropagation();
        }
        callbackRef.current(event);
        break;
      }
    }
  }, [keys, enabled, preventDefault, stopPropagation]);

  useEffect(() => {
    const targetElement = target || window;
    targetElement.addEventListener('keydown', handleKeyDown);
    return () => {
      targetElement.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, target]);
};

/**
 * useFormSaveHotkey — Raccourci spécialisé Ctrl+S / Cmd+S pour sauvegarder un formulaire.
 * 
 * @param {Function} onSave - Callback de sauvegarde
 * @param {boolean} enabled - Activation du raccourci
 */
export const useFormSaveHotkey = (onSave, enabled = true) => {
  useHotkeys(['ctrl+s', 'meta+s'], (e) => {
    if (typeof onSave === 'function') {
      onSave(e);
    }
  }, { enabled, preventDefault: true });
};

export default useHotkeys;
